// assets/AllCourses/Exercises/CircleTheCorrectWordExercise.jsx

import React from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { StringField, TextareaField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";
import useExerciseEngine from "@/hooks/useExerciseEngine";

export default function CircleTheCorrectWordExercise({
  exercise = {},
  userAnswer = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResult = false,
  onComplete = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.wordChoiceQuestions)
    ? exercise.wordChoiceQuestions
    : [];

  /* ================= ENGINE ================= */
  const {
  answers,
  setAnswer,
  submitIfReady,
  getOptionState,
} = useExerciseEngine({
  questions: questions.map((q) => ({
    ...q,
    type: "single",
    answer: q.correct,
  })),
  showResult,

  // ✅ VERY IMPORTANT (fixes back button issue)
  externalAnswers: userAnswer || {},

  // ✅ IMPORTANT (format for parent)
  onComplete: (engineData) => {
    onComplete(engineData.answers); 
  },
});

  /* ================= EDIT HELPERS ================= */
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("wordChoiceQuestions", next);
  };

  const updateQuestionField = (idx, field, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    next[idx] = {
      sentence: "",
      correct: "",
      requireCapitalization: false,
      ...next[idx],
      [field]: value,
    };

    updateQuestions(next);
  };

  const addQuestion = () => {
    if (!isEditMode) return;
    updateQuestions([
      ...questions,
      { sentence: "", correct: "", requireCapitalization: false },
    ]);
  };

  const removeQuestion = (idx) => {
    if (!isEditMode) return;
    updateQuestions(questions.filter((_, i) => i !== idx));
  };

  /* ================= STUDENT CLICK ================= */
  const handleWordClick = (qIdx, word) => {
    if (isEditMode || showResult) return;

    setAnswer(qIdx, word, {
      questionIndex: qIdx,
    });

    submitIfReady();
  };

  /* ================= RENDER SENTENCE ================= */
  const renderSentence = (q, qIdx) => {
    if (!q?.sentence) {
      return (
        <span className="text-muted-foreground">
          No sentence provided.
        </span>
      );
    }

    const parts = q.sentence.split(/[\(\)]/);

    return parts.map((part, idx) => {
      if (idx % 2 === 1) {
        const options = part
          .split("/")
          .map((o) => o.trim())
          .filter(Boolean);

        return (
          <span key={idx} className="inline-flex flex-wrap gap-2 mx-2">
            {options.map((word) => {
              const selected = answers[qIdx];

              const state = getOptionState(
                selected,
                word,
                q.correct
              );

              return (
                <button
                  key={word}
                  type="button"
                  onClick={() => handleWordClick(qIdx, word)}
                  disabled={showResult}
                  className={cn(
                    "px-4 py-1.5 rounded-full text-sm border transition cursor-pointer hover:bg-muted/60",
                    state.className,
                    showResult && "cursor-not-allowed opacity-90"
                  )}
                >
                  {word}
                </button>
              );
            })}
          </span>
        );
      }

      return <span key={idx}>{part}</span>;
    });
  };

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, idx) => (
          <Card key={idx} className="p-5">
            <div className="space-y-4">
              <TextareaField
                label={`Sentence ${idx + 1}`}
                value={q.sentence || ""}
                onChange={(v) =>
                  updateQuestionField(idx, "sentence", v)
                }
                placeholder="The (big/small) house is beautiful."
                rows={3}
                required
                error={
                  errors?.[
                    `exercise-${exercise.index}-wc-sentence-${idx}`
                  ]
                }
              />

              <StringField
                label="Correct Answer"
                value={q.correct || ""}
                onChange={(v) =>
                  updateQuestionField(idx, "correct", v)
                }
                placeholder="big"
                required
                error={
                  errors?.[
                    `exercise-${exercise.index}-wc-correct-${idx}`
                  ]
                }
              />

              <CapitalizationRule
                checked={!!q.requireCapitalization}
                onChange={(c) =>
                  updateQuestionField(
                    idx,
                    "requireCapitalization",
                    c
                  )
                }
              />

              <Button
                size="sm"
                variant="destructive"
                onClick={() => removeQuestion(idx)}
              >
                Remove Question
              </Button>
            </div>
          </Card>
        ))}

        <Button variant="outline" onClick={addQuestion}>
          Add Question
        </Button>
      </div>
    );
  }

  /* ================= EMPTY ================= */
  if (!questions.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>
            No questions available.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  /* ================= STUDENT MODE ================= */
  return (
    <Card className="max-w-3xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle className="text-base">
          Choose the Correct Word
        </CardTitle>
        <CardDescription>
          Click the correct word in each sentence.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {questions.map((q, idx) => (
          <div
            key={idx}
            className="rounded-xl border bg-muted/40 p-5 text-base"
          >
            {renderSentence(q, idx)}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}